import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';

import { graphql } from '@e-socna/data-layer';

import { useGraphQLMutation } from '../../../hooks/useGraphQLMutation.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';

import { missionDraftForm2ApiMapper } from '../mappers/mission-draft.form2api.mapper';
import type { MissionForm_Mission } from '../fragments/mission.fragment';
import { MessageList } from '../../../components/MessageList';

export const MissionForm_CodingConfigStep_CodeSuggestion_QueryDocument =
  graphql(`
    query MissionForm_CodingConfigStep_CodeSuggestion_Query(
      $mission: MissionInput!
    ) {
      suggestMissionCode(mission: $mission)
    }
  `);

export const useCodeSuggestion = () => {
  const { codingStrategiesCodeMap } = useReferencesContext();
  const { enqueueSnackbar } = useSnackbar();

  const { values, setFieldValue } = useFormikContext<MissionForm_Mission>();

  const [isLoading, setIsLoading] = useState(false);

  const { mutate: getCodeSuggestionQuery } = useGraphQLMutation(
    MissionForm_CodingConfigStep_CodeSuggestion_QueryDocument,
    {
      onMutate: () => {
        setIsLoading(true);
      },
      onSuccess: (data) => {
        void setFieldValue('codingConfig.code', data.suggestMissionCode);
      },
      onError: (error: unknown) => {
        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
          },
        );
      },
      onSettled: () => {
        setIsLoading(false);
      },
    },
  );

  const codeSuggestionHandler = useCallback(() => {
    const mission = missionDraftForm2ApiMapper(
      values,
      codingStrategiesCodeMap.CodingConfigAutomatic._id,
    );

    getCodeSuggestionQuery({ mission });
  }, [
    codingStrategiesCodeMap.CodingConfigAutomatic._id,
    getCodeSuggestionQuery,
    values,
  ]);

  return {
    isLoading,
    codeSuggestionHandler,
  };
};
